import React, { FC, useEffect, useState } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { JoyKitThemeProvider, Theme } from '@withjoy/joykit';
import useSet from '@shared/utils/hooks/useSet';
import globalWindow from '@shared/core/globals';
import { layoutConfigs } from '@apps/guest/packages/layout-engine/layouts/layout.constants';
import { GuestSiteTypographyOverrideProps } from './GuestSiteTypographyOverride.types';
import { useEventProvider } from '@apps/guest/packages/layout-engine/layouts/EventProvider/EventProvider';
import { useFeatureValue } from '@shared/core/featureFlags';

export const GuestSiteTypographyOverride: FC<GuestSiteTypographyOverrideProps> = props => {
  const { variant, category, children } = props;

  const { event } = useEventProvider();
  const { eventDesign } = event;

  const layoutConfig = eventDesign?.websiteLayout.layoutType === 'brannan' ? layoutConfigs.brannan : layoutConfigs.aloha;
  const [theme, setTheme] = useState<Theme>(eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme);
  const [_, { addMultiple: addFonts, has: hasFont }] = useSet();

  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';

  useEffect(() => {
    if (eventDesign && layoutConfig && fontPackExperimentEnabled) {
      // Collect fonts to load
      const fontToLoad = eventDesign?.fonts?.find(font => font.category === category)?.font?.fontFamily;
      const fontPackFontFamilies = fontToLoad ? [fontToLoad] : [];

      // Update JoyKit with `eventDesign` applied to a layout, otherwise use the default layout theme
      const updateTheme = () => {
        const currentTheme = layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme);

        const fontToUpdate = eventDesign?.fonts?.find(font => font.category === category);
        const updatedTheme: Theme = {
          ...currentTheme,
          typography: {
            ...currentTheme.typography,
            variants: {
              ...currentTheme.typography.variants,
              [variant]: {
                ...currentTheme.typography.variants[variant],
                fontFamily: fontToUpdate?.font?.fontFamily || currentTheme.typography.variants[variant].fontFamily,
                fontSize: fontToUpdate?.size ? pxToRem(fontToUpdate.size) : currentTheme.typography.variants[variant].fontSize
              }
            }
          }
        };

        setTheme({ ...updatedTheme });
      };
      if (globalWindow.WebFont) {
        const getFontsUnloaded = fontPackFontFamilies.filter((font: string) => !hasFont(font));
        if (!getFontsUnloaded.length) {
          updateTheme();
          return;
        }

        // 1. Attempt to load fonts first to prevent FOUT
        // 2. Update theme regardless of font load success
        globalWindow.WebFont?.load({
          google: {
            families: getFontsUnloaded
          },
          fontactive: () => {
            addFonts(getFontsUnloaded);
            updateTheme();
          },

          fontinactive: () => {
            updateTheme();
          }
        });
      } else {
        updateTheme();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDesign, eventDesign?.fonts, layoutConfig, setTheme, fontPackExperimentEnabled]);

  return fontPackExperimentEnabled ? <JoyKitThemeProvider theme={theme}>{children}</JoyKitThemeProvider> : <>{children}</>;
};
